// Mixins
import { socialClubMixins } from "@/app/mixins/components/social-club-mixins"
import { userMixins } from "@/app/mixins/modules/user-mixins"
import { dateMixins } from "@/app/mixins/utils/date-mixins"
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins"

// Services
import CommentService from "@/app/services/main/comment/comment-service.js"

// Constants
import { ERROR_MSG } from "@/app/constants/status-constants"
import { STATUS_CODES, CONTENT_TYPE } from "@/app/constants/status-constants"

// Third party libraries
const vue2Dropzone = () => import(/*webpackPrefetch: true, webpackChunkName: "vue-dropzone"*/ "vue2-dropzone")
const VueGallerySlideshow = () =>
	import(/* webpackPrefetch: true, webpackChunkName: "vue-gallery-slideshow"*/ "vue-gallery-slideshow")

export default {
	name: "SocialClubEditableComment",
	components: {
		vueDropZone: vue2Dropzone,
		VueGallerySlideshow,
	},
	mixins: [socialClubMixins, userMixins, dateMixins, imageCompressorMixins],
	props: {
		commentContent: {
			type: Object,
			required: true,
		},
		firstOpenCommentFlag: {
			type: Boolean,
			required: true,
			default: true,
		},
		feedContent: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			editMode: false,
			userInput: this.commentContent.content,
			imgLinks: this.commentContent.mediaFile.location,
			initialLinks: this.commentContent.mediaFile.location,
			commentIndex: null,
			dropOptions: {
				url: "https://httpbin.org/post", // endpoint where files needs to be sent. Currently mocked.
				thumbnailWidth: null,
				thumbnailHeight: null,
				maxFilesize: 20,
				maxThumbnailFilesize: 20,
				acceptedFiles: ".svg, .png, .jpg, .gif, .jpeg",
				headers: { Authorization: "Bearer mock authorization" },
				uploadMultiple: false,
				autoProcessQueue: false,
				maxFiles: 1,
				addRemoveLinks: true,
				ignoreHiddenFiles: true,
				clickable: `#editCommentUploader-${this.commentContent.id}-${this.feedId}`,
				dictFileTooBig: "File Size must not exceed 20 MB.",
				dictInvalidFileType: "Unsupported File Format.",
				dictRemoveFile: "<i class='fa fa-close'></i>",
			},
			isEditingComment: false,
			contentType: CONTENT_TYPE,
		}
	},
	computed: {
		isSameContent() {
			return this.userInput === this.commentContent.content
		},
	},
	methods: {
		executeDropdownAction(action) {
			if (action === "edit") {
				this.editMode = true
				if (this.commentContent.mediaFile.location) {
					this.$refs.editCommentDropZone.manuallyAddFile(
						{ size: 5, name: "Stock Image", type: "image/png" },
						this.commentContent.mediaFile.location
					)
				}
				this.triggerOpenCommentFlag(false)
			} else if (action === "delete") {
				this.showDeleteAlert()
			}
		},
		triggerOpenCommentFlag(flag) {
			this.$emit("triggerOpenCommentFlag", flag)
		},
		cancelEdit() {
			const { content, imageLinks } = this.commentContent

			this.editMode = false
			this.userInput = content
			this.imgLinks = imageLinks
			this.triggerOpenCommentFlag(true)
			this.$refs.editCommentDropZone.removeAllFiles(true)
			this.$store.commit("resetEditComments")
		},
		async submitEdit() {
			if (!this.isEditingComment) {
				this.isEditingComment = true
				const addingImages = this.$refs.editCommentDropZone.getAcceptedFiles()
				const manuallyAddedFiles = this.$refs.editCommentDropZone.getRejectedFiles()

				const commentDetails = {
					formDetails: { commentId: this.commentContent.id },
				}

				if (this.userInput !== this.commentContent.content) {
					commentDetails.formDetails.content = this.userInput
				}

				if (manuallyAddedFiles.length === 0 && addingImages.length === 0) {
					commentDetails.formDetails.removeImage = true
				}

				if (addingImages.length > 0) {
					commentDetails.image = await this.compressImage(addingImages[0])
				}

				const res = await CommentService.updateComment(commentDetails)
				if (res.status === STATUS_CODES.SUCCESS) {
					const {
						data: { comment },
					} = res

					this.$emit("updateCommentCallback", comment)
					this.userInput = comment.content
					this.editMode = false
					this.$refs.editCommentDropZone.removeAllFiles(true)
					this.$store.commit("setCommentHasChange", false)
					this.$store.commit("setCommentImageHasChanges", false)
				} else {
					const { ERROR_UPDATE_COMMENT } = this.toastMessages.comment()
					this.showAPIErrorToast(ERROR_UPDATE_COMMENT, res)
				}

				this.isEditingComment = false
			}
		},
		async showDeleteAlert() {
			const { value } = await this.$swal({
				imageHeight: "30px",
				imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
				imageAlt: "Confirm Dog",
				titleText: `Delete Comment`,
				html: `<p>Are you sure you want to delete this comment?</p>`,
				confirmButtonText: "Confirm",
				cancelButtonText: "Cancel",
				showCancelButton: true,
				customClass: {
					header: "header-social-club",
					title: "title-social-club",
				},
				reverseButtons: true,
			})

			if (value) {
				await CommentService.deleteComment(this.commentContent.id)
				this.$emit("deleteCommentCallback", this.commentContent)
			}
		},
		triggerEditCommentHasChange() {
			const payload = !this.isSameContent

			this.$store.commit("setCommentHasChange", payload)
		},
		triggerCommentImageHasChange() {
			// Algorithm for comparing image contents goes here. Refactor when API is finalized.
			this.$store.commit("setCommentImageHasChanges", true)
		},
		onDropZoneError(file, message) {
			if (message !== ERROR_MSG.DROPZONE_UPLOAD_CANCELLED) {
				this.showCustomErrorToast(ERROR_MSG.DROPZONE_UPLOAD_ERROR_TITLE, message)
			}

			this.$refs.editCommentDropZone.removeFile(file)
		},
		emitReport(content, type) {
			this.$emit("emitReport", { ...content, type })
		},
	},
}
